import * as vp from 'vopay';
import { FunctionComponent, useState, useContext, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import SignersContext, { SignersCtx } from './SignersContext';
import { SignersDialogueHandler, DialogueHandlerProps } from './type';

import useStorage from '@/hooks/useStorage';
import AccountContext from '../AccountContext';
import usePostDeleteSigner from '@/hooks/useApi/usePostDeleteSigner';
import { usePartnerData } from '@/providers/PartnerData.provider';
import SignersDialogue from './SignersDialogue';
import styles from './Signers.module.scss';

const { Box, Button, SubTitle, Table, Icon, Toast, Badge } = vp;

type SignersProps = {
  agreementDetails: vp.AgreementDetailsResponse;
  accountToken: string;
  isAddSignerToastVisible: boolean;
  signingAuthorityInfo: vp.SigningAuthorityInfoResponse;
  handleSignerLists: () => void;
};

type FC = FunctionComponent<SignersProps>;

const SignerContainer: FC = ({
  agreementDetails,
  accountToken,
  isAddSignerToastVisible,
  signingAuthorityInfo,
  handleSignerLists,
}: SignersProps) => {
  const history = useHistory();
  const { details } = useContext(AccountContext);
  const AccountID =
    Object.values(details)
      .map((data) => data.accountId)
      .pop() || '';

  const [deleteSigner, postDeleteSigner] = usePostDeleteSigner({ bearer: accountToken });
  const [showDeleteSignerConfirmAlert, setShowDeleteSignerConfirmAlert] = useState(false);
  const [showDeleteSignerErrorAlert, setShowDeleteSignerErrorAlert] = useState(false);
  const [shouldDeleteSigner, setShouldDeleteSigner] = useState(false);
  const [deleteSignerError, setDeleteSignerError] = useState('');
  const [showEditSignerAlert, setShowEditSignerAlert] = useState(false);
  const [showDeleteSignerAlert, setShowDeleteSignerAlert] = useState(false);
  const [shouldShowMaximumSignerAlert, setShouldShowMaximumSignerAlert] = useState(false);
  const [signingAuthorityID, setSigningAuthorityID] = useState('');
  const [signerName, setSignerName] = useState('');
  const [isToastVisible, setIsToastVisible] = useState(false);
  const {
    signerCreation: { storeSignerCreation, clearSignerCreation },
  } = useStorage();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const Signers: SignersCtx = {
    showDeleteSignerConfirmAlert,
    showDeleteSignerErrorAlert,
    shouldShowMaximumSignerAlert,
    showEditSignerAlert,
    showDeleteSignerAlert,
    deleteSignerError,
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<SignersDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: SignersDialogueHandler = {
        delete: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShowDeleteSignerErrorAlert(false);
              setDeleteSignerError('');
            },
          },
        },
        alertDelete: {
          back: {
            default: () => {
              setShowDeleteSignerConfirmAlert(false);
            },
          },
          confirm: {
            default: () => {
              setShowDeleteSignerConfirmAlert(false);
              setShouldDeleteSigner(true);
              postDeleteSigner({
                SigningAuthorityID: signingAuthorityID,
              });
            },
          },
        },
        warning: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShouldShowMaximumSignerAlert(false);
            },
          },
        },
        noEdit: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShowEditSignerAlert(false);
            },
          },
        },
        noDelete: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShowDeleteSignerAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (shouldDeleteSigner) {
      setShouldDeleteSigner(false);
      const { Success, ErrorMessage } = deleteSigner;
      if (Success === true) {
        setIsToastVisible(true);

        setTimeout(() => {
          storeSignerCreation('signerToast');
        }, 350);

        setTimeout(() => {
          handleSignerLists();
          setIsToastVisible(false);
          clearSignerCreation();
        }, 400);
      } else {
        setDeleteSignerError(ErrorMessage);
        setShowDeleteSignerErrorAlert(true);
      }
    }
  }, [deleteSigner]);

  const tableProps: vp.TableProps = {
    name: 'signers-list',
    columns: {
      signerName: { header: 'Signer Name', align: 'left' },
      title: { header: 'Title', align: 'left' },
      email: { header: 'Email', align: 'left' },
      signatureStatus: { header: 'Signature Status', align: 'left' },
      view: { header: '' },
    },
    rows:
      Object.values(signingAuthorityInfo?.SigningAuthority || {}).map((signer) => ({
        signerName: signer.FullName,
        title: signer.Occupation,
        email: signer.EmailAddress,
        signatureStatus: (
          <Badge
            label={vp.capitalizeFirstLetterOfWords(signer.DocumentSigned === '1' ? 'Signed' : 'Not Signed')}
            color={signer.DocumentSigned === '1' ? 'positive' : 'neutral'}
            hasIcon
          />
        ),
        view: (
          <div className={styles.iconContainer}>
            {agreementDetails.AgreementStatus === 'Agreement Sent' ||
            agreementDetails.AgreementStatus === 'Agreement Viewed' ||
            agreementDetails.AgreementStatus === 'Agreement Completed' ? (
              <Box jc="center">
                <Button
                  id="show-edit-fail-dialogue-btn"
                  label=""
                  color="none"
                  icon="pen"
                  iconWidth="12px"
                  iconLocation="center"
                  noPadding
                  noShadow
                  isDisabled={isRoleViewOnly}
                  onClick={() => {
                    setShowEditSignerAlert(true);
                  }}
                />
              </Box>
            ) : (
              <Link
                to={{
                  pathname: `/accounts/${AccountID}/edit/${signer.ID}`,
                  search: new URLSearchParams({
                    signers: Object.values(signingAuthorityInfo.SigningAuthority)
                      .map((s) => s.EmailAddress)
                      .filter((email) => email)
                      .join(','),
                    accountToken,
                    FullName: signer.FullName,
                    EmailAddress: signer.EmailAddress,
                    DOB: signer.DOB,
                    Occupation: signer.Occupation,
                    HomeAddress: signer.HomeAddress,
                    ID: signer.ID,
                  }).toString(),
                }}
              >
                <Box jc="center">
                  <Icon icon="pen" width="12px" />
                </Box>
              </Link>
            )}
            <Link
              to={{
                pathname: `/accounts/${AccountID}/view/${signer.ID}`,
                search: new URLSearchParams({
                  FullName: signer.FullName,
                  EmailAddress: signer.EmailAddress,
                  DOB: signer.DOB,
                  Occupation: signer.Occupation,
                  HomeAddress: signer.HomeAddress,
                }).toString(),
              }}
            >
              <Box jc="center">
                <span>
                  <Icon icon="document" width="15px" />
                </span>
              </Box>
            </Link>

            <Box jc="center">
              {agreementDetails.AgreementStatus === 'Agreement Sent' ||
              agreementDetails.AgreementStatus === 'Agreement Viewed' ||
              agreementDetails.AgreementStatus === 'Agreement Completed' ? (
                <Button
                  id="show-delete-fail-dialogue-btn"
                  label=""
                  color="none"
                  icon="can"
                  iconSize="sm"
                  iconLocation="center"
                  noPadding
                  noShadow
                  isDisabled={isRoleViewOnly}
                  onClick={() => {
                    setShowDeleteSignerAlert(true);
                  }}
                />
              ) : (
                <Button
                  id={`delete-signer-${signer.ID}`}
                  label=""
                  color="none"
                  size="lg"
                  icon="can"
                  iconLocation="center"
                  iconSize="sm"
                  noShadow
                  noPadding
                  isDisabled={isRoleViewOnly}
                  onClick={() => {
                    setSigningAuthorityID(signer.ID);
                    setSignerName(signer.FullName);
                    setTimeout(() => {
                      setShowDeleteSignerConfirmAlert(true);
                    }, 300);
                  }}
                />
              )}
            </Box>
          </div>
        ),
      })) || [],
  };

  return (
    <>
      {signingAuthorityInfo?.ErrorMessage === 'There is no signing authority information for this account' ? (
        <div className={styles.container}>
          <h2 className={styles.containerTitle}>Signers</h2>
          <Box>
            There are no signers associated with this account. Please add signers to start agreement generation.
          </Box>
          <Button
            id="add-signer-btn"
            label="Add Signer"
            color="primary"
            width="228px"
            noShadow
            round
            isDisabled={isRoleViewOnly}
            onClick={() => {
              history.push('/accounts/signer-create');
            }}
          />
        </div>
      ) : signingAuthorityInfo?.Success ? (
        <SignersContext.Provider value={Signers}>
          <Box mt="lg" mb="md">
            <SubTitle subTitle="Signers" />
          </Box>
          <div className={styles.tableContainer}>
            <Table {...tableProps} />
          </div>
          <Box pt="lg">
            <Button
              id="add-signer-btn"
              label="Add Signer"
              color="primary"
              width="228px"
              noShadow
              round
              isDisabled={isRoleViewOnly}
              onClick={() => {
                if (Object.values(signingAuthorityInfo?.SigningAuthority || {}).length >= 5) {
                  setShouldShowMaximumSignerAlert(true);
                } else {
                  history.push('/accounts/signer-create');
                }
              }}
            />
          </Box>
          <Toast isVisible={isAddSignerToastVisible} label="Signers have been added!" />
          <Toast isVisible={isToastVisible} label={`'${signerName}' has successfully be deleted as a signor.`} />
          <SignersDialogue handleDialogue={handlers.handleDialogue} />
        </SignersContext.Provider>
      ) : (
        <div className={styles.container}>
          <h2 className={styles.containerTitle}>Signers</h2>
          <Box>{signingAuthorityInfo.ErrorMessage}</Box>
        </div>
      )}
    </>
  );
};

export default SignerContainer;
